// Global Instructions Rule Applied!

import { FieldTypeEnum, SelectTypeEnum } from '../../../core/components/InputManager/types.ts'
import type { CountryChangePatch } from './types.ts'
import { DealFields } from '../../../state_manager/data_models/deal_record.ts'
import {
  LOOKUP_CATEGORY_INDUSTRY,
  LOOKUP_CATEGORY_FUNDING_STAGE,
  LOOKUP_CATEGORY_SECURITY_TYPE
} from '../../../state_manager/constants/lookupCategories.ts'

/**
 * Maps DealsTable column definitions to InputManager field configurations
 * Handles dynamic field type detection and option mapping
 */

/**
 * Maps a single column to InputManager field configuration
 */
export function mapColumnToInputManagerField(column: any, record: any, helpers: any, fieldType: FieldTypeEnum): any {
  const { getCurrencyById, getPipelineStageOptions, getPipelineStageOptionsForDeal, getRoleOptions } = helpers

  const columnKey = column.dataIndex || column.key
  const columnTitle = column.title
  const isRequired = column.required || false
  const isEditable = column.editable !== false

  // Get current value from record
  const getCurrentValue = () => {
    if (columnKey === DealFields.prog__valuation || columnKey === 'valuation') {
      return {
        preMoneyValue: record[DealFields.rounds__pre_money_valuation] || null,
        postMoneyValue: record[DealFields.rounds__post_money_valuation] || null,
        currencySymbol: getCurrencyById(record[DealFields.deals__currency])?.symbol || '$'
      }
    }

    if (columnKey === 'valuation_round' || columnKey === 'latest_round') {
      return record[DealFields.rounds__name] || ''
    }

    if (columnKey === 'funding_stage') {
      return record[DealFields.rounds__funding_stage] || ''
    }

    // For pipeline_deals__stage__label, return the UUID value (pipeline_deals__stage) instead of label
    if (columnKey === DealFields.pipeline_deals__stage__label) {
      return record[DealFields.pipeline_deals__stage] || ''
    }

    // For city field, construct city object from code and label if available
    if (columnKey === DealFields.accounts__business_city_label) {
      const cityCode = record[DealFields.accounts__business_city_code]
      const cityLabel = record[DealFields.accounts__business_city_label]
      const countryCode = record[DealFields.accounts__business_country_code]
      const state = record[DealFields.accounts__business_stateprovince]

      // If we have a city code and label, construct the city object
      if (cityCode && cityLabel) {
        // Check if value is already an object
        if (typeof cityLabel === 'object' && cityLabel !== null) {
          return cityLabel
        }
        // Construct city object from code and label
        return {
          value: cityCode,
          name: cityLabel,
          label: cityLabel,
          data: { countryCode: countryCode || null, state: state || null }
        }
      }
      // Return existing value (could be object or string)
      return cityLabel || ''
    }

    // For country field, construct country object from code and label if available
    if (columnKey === DealFields.accounts__business_country_label) {
      const countryCode = record[DealFields.accounts__business_country_code]
      const countryLabel = record[DealFields.accounts__business_country_label]

      // If we have a country code and label, construct the country object
      if (countryCode && countryLabel) {
        // Check if value is already an object
        if (typeof countryLabel === 'object' && countryLabel !== null) {
          return countryLabel
        }
        // Construct country object from code and label
        return {
          value: countryCode,
          label: countryLabel
        }
      }
      // Return existing value (could be object or string)
      return countryLabel || ''
    }

    if (columnKey === 'role') {
      const roles = Array.isArray(record.prog__account_roles) ? record.prog__account_roles : record[columnKey] || []
      if (!Array.isArray(roles)) {
        return []
      }
      return roles
        .map((role: any) => {
          if (typeof role === 'string') return role
          if (role && typeof role === 'object' && role.account_roles__account_role_choice_id) {
            return role.account_roles__account_role_choice_id
          }
          return null
        })
        .filter((id: any): id is string => id !== null)
    }

    return record[columnKey] || ''
  }

  // Get currency symbol for currency fields
  const getCurrencySymbol = () => {
    if (fieldType === 'currency') {
      return getCurrencyById(record[DealFields.deals__currency])?.symbol || '$'
    }
    return '$'
  }

  // Get country filter for city field
  const getCountryFilter = () => {
    if (columnKey === DealFields.accounts__business_city_label) {
      return record[DealFields.accounts__business_country_code] || null
    }
    return null
  }

  const fieldKey =
    columnKey === DealFields.pipeline_deals__stage__label
      ? DealFields.pipeline_deals__stage
      : columnKey === 'role'
        ? 'accounts__roles'
        : columnKey === 'funding_stage'
          ? DealFields.rounds__funding_stage
          : columnKey === 'latest_round' || columnKey === 'valuation_round'
            ? DealFields.rounds__name
            : columnKey === 'valuation'
              ? DealFields.prog__valuation
              : columnKey

  const roundId = record[DealFields.rounds__id] || record.round || null
  const isRoundField =
    columnKey === DealFields.prog__valuation ||
    columnKey === 'valuation' ||
    columnKey === DealFields.rounds__funding_stage ||
    columnKey === DealFields.rounds__security_type ||
    columnKey === DealFields.rounds__raise_amount ||
    columnKey === 'funding_stage' ||
    columnKey === 'latest_round' ||
    columnKey === 'valuation_round'

  const selectConfig = () => {
    switch (fieldKey) {
      case DealFields.accounts__industry:
        return {
          selectType: SelectTypeEnum.LOOKUP,
          lookupCategory: LOOKUP_CATEGORY_INDUSTRY
        }
      case DealFields.pipeline_deals__stage:
      case DealFields.pipeline_deals__stage__label:
        return {
          selectType: SelectTypeEnum.FILTER,
          options: getPipelineStageOptionsForDeal
            ? getPipelineStageOptionsForDeal(record)
            : getPipelineStageOptions
              ? getPipelineStageOptions()
              : []
        }
      case DealFields.rounds__funding_stage:
        return {
          selectType: SelectTypeEnum.LOOKUP,
          lookupCategory: LOOKUP_CATEGORY_FUNDING_STAGE
        }
      case DealFields.rounds__security_type:
        return {
          selectType: SelectTypeEnum.LOOKUP,
          lookupCategory: LOOKUP_CATEGORY_SECURITY_TYPE
        }
      case 'accounts__roles':
        return {
          selectType: null,
          options: getRoleOptions ? getRoleOptions() : []
        }
      default:
        return {
          selectType: null,
          options: []
        }
    }
  }

  const baseConfig = {
    key: fieldKey,
    label: columnTitle,
    value: getCurrentValue(),
    type: fieldType,
    required: isRequired,
    editable: isRoundField ? isEditable && !!roundId : isEditable
  }

  // Add type-specific configurations
  switch (fieldType) {
    case 'currency':
      const currencyConfig: any = {
        ...baseConfig,
        currencySymbol: getCurrencySymbol(),
        min: 0,
        step: 0.01
      }
      // Set patchId and patchEndpoint for Round currency fields
      if (isRoundField && roundId) {
        currencyConfig.patchEndpoint = 'rounds'
        currencyConfig.patchId = roundId
      }
      return currencyConfig

    case FieldTypeEnum.SELECT:
      // For pipeline_deals__stage, configure to patch pipeline_deals endpoint
      const selectFieldConfig: any = {
        ...baseConfig,
        ...selectConfig(),
        allowClear: true
      }

      // If this is pipeline_deals__stage, configure it to patch pipeline_deals table
      if (fieldKey === DealFields.pipeline_deals__stage) {
        const pipelineDealsId =
          record?.[DealFields.pipeline_deals__id] || (record as Record<string, any>)?.pipeline_deals__id || null
        selectFieldConfig.patchEndpoint = 'pipeline-deals'
        // Use pipeline_deals__id from record as the patch ID
        if (pipelineDealsId) {
          selectFieldConfig.patchId = pipelineDealsId
        }
        // Commit gate (useCommitDealGate): the grid row carries the commitment
        // and round fields the gate reads, so merge it under the cell values.
        if (typeof helpers?.confirmStageChange === 'function') {
          selectFieldConfig.confirmPatch = (value: any, currentRecord: any) =>
            helpers.confirmStageChange({ ...record, ...(currentRecord || {}) }, value)
        }
        selectFieldConfig.patchFields = [
          { sourceKey: 'value', targetField: 'stage' },
          { sourceKey: 'stageDate', targetField: 'stage_date' }
        ]
        // Also update stage_date when stage changes
        selectFieldConfig.valueTransformer = (value: any, currentRecord: any) => {
          const currentPipelineDealsId =
            pipelineDealsId ||
            currentRecord?.[DealFields.pipeline_deals__id] ||
            currentRecord?.pipeline_deals__id ||
            null
          const stageDate = new Date().toISOString().split('T')[0]
          const result: any = {
            // API fields (mapped by patchFields)
            value: value, // Will be mapped to 'stage'
            stageDate: stageDate, // Will be mapped to 'stage_date'
            // Redux fields (direct mapping)
            [DealFields.pipeline_deals__stage]: value,
            [DealFields.pipeline_deals__stage_date]: stageDate,
            [DealFields.pipeline_config__id]: value // Same as pipeline_deals__stage, needed for Kanban board column assignment
          }
          if (currentPipelineDealsId) {
            result.id = currentPipelineDealsId
          }
          return result
        }

        // Set displayValue to show label instead of UUID when not editing
        // Use the label field if available, otherwise find label from options
        if (record[DealFields.pipeline_deals__stage__label]) {
          selectFieldConfig.displayValue = record[DealFields.pipeline_deals__stage__label]
        } else if (selectFieldConfig.options && selectFieldConfig.value) {
          const selectedOption = selectFieldConfig.options.find((opt: any) => opt.value === selectFieldConfig.value)
          if (selectedOption) {
            selectFieldConfig.displayValue = selectedOption.label
          }
        }
      } else if (fieldKey === DealFields.rounds__funding_stage || fieldKey === DealFields.rounds__security_type) {
        // Configure Round fields to patch rounds endpoint
        selectFieldConfig.patchEndpoint = 'rounds'
        // Set patchId to roundId for Round fields
        if (roundId) {
          selectFieldConfig.patchId = roundId
        }
      }

      if (columnKey === 'role') {
        selectFieldConfig.mode = 'multiple'
        selectFieldConfig.tagDisplay = true
        selectFieldConfig.patchEndpoint = 'accounts'
        selectFieldConfig.valueTransformer = (value: any, currentRecord: any) => {
          const newRoleIds = Array.isArray(value) ? value : []
          const currentRoles = Array.isArray(currentRecord?.prog__account_roles)
            ? currentRecord.prog__account_roles
            : []
          const currentRoleMap = new Map<string, any>()
          const newRoleSet = new Set(newRoleIds.filter((roleId: any) => !!roleId))

          currentRoles.forEach((role: any) => {
            if (typeof role === 'object' && role?.account_roles__account_role_choice_id) {
              currentRoleMap.set(role.account_roles__account_role_choice_id, role)
            } else if (typeof role === 'string') {
              currentRoleMap.set(role, { account_roles__account_role_choice_id: role })
            }
          })

          const rolesWithActions: any[] = []

          newRoleIds.forEach((roleChoiceId: string) => {
            if (!roleChoiceId) return
            const currentRole = currentRoleMap.get(roleChoiceId)
            if (currentRole?.account_roles__id) {
              rolesWithActions.push({
                account_roles__id: currentRole.account_roles__id,
                account_roles__account_role_choice_id: roleChoiceId,
                prog__account_roles__action: 'update'
              })
            } else {
              rolesWithActions.push({
                account_roles__id: crypto.randomUUID(),
                account_roles__account_role_choice_id: roleChoiceId,
                prog__account_roles__action: 'insert'
              })
            }
          })

          currentRoleMap.forEach((role: any, roleChoiceId: string) => {
            if (!newRoleSet.has(roleChoiceId) && role?.account_roles__id) {
              rolesWithActions.push({
                account_roles__id: role.account_roles__id,
                prog__account_roles__action: 'delete'
              })
            }
          })

          return {
            prog__account_roles: rolesWithActions
          }
        }
      }

      if (fieldKey === DealFields.rounds__funding_stage && record[DealFields.rounds__funding_stage__label]) {
        selectFieldConfig.displayValue = record[DealFields.rounds__funding_stage__label]
      }
      if (fieldKey === DealFields.rounds__security_type && record[DealFields.rounds__security_type__label]) {
        selectFieldConfig.displayValue = record[DealFields.rounds__security_type__label]
      }

      return selectFieldConfig

    case FieldTypeEnum.COMPANY:
      return {
        ...baseConfig,
        contextKey: `cell-${record.id}-${columnKey}`
      }

    case FieldTypeEnum.CITY:
      return {
        ...baseConfig,
        country: getCountryFilter(),
        contextKey: `cell-${record.id}-${columnKey}`,
        // Multi-field PATCH: Send city code, city name, and country code to API
        patchFields: [
          { sourceKey: 'cityCode', targetField: 'business_city_code' },
          { sourceKey: 'cityName', targetField: 'business_city_label' },
          { sourceKey: 'countryCode', targetField: 'business_country_code' },
          { sourceKey: 'state', targetField: 'business_stateprovince' }
        ],
        patchEndpoint: 'accounts',
        // Transform city object into API fields + Redux fields
        valueTransformer: (cityObject: any, currentRecord: any) => {
          // Handle clearing: when cityObject is empty string/null/undefined, set values to null explicitly
          const isClearing = !cityObject || cityObject === '' || (typeof cityObject === 'object' && !cityObject.value)

          // Extract values from CitySelect object: { label, value, name, countryCode, country, state }
          // Explicitly set to null when clearing (not undefined) so it's included in JSON
          const cityCode = isClearing ? null : (cityObject?.value ?? null)
          const cityLabel = isClearing ? null : (cityObject?.name ?? null) // We're using name as label includes (CityName (CityCode)) and name is the only (CityName)

          // Only include countryCode when selecting a city (not when clearing)
          // When clearing city, preserve the existing country
          const countryCode = isClearing
            ? undefined
            : (cityObject?.data?.countryCode ?? cityObject?.countryCode ?? undefined)
          const state = isClearing ? null : (cityObject?.data?.state ?? cityObject?.state ?? null)

          const result: any = {
            // API fields (mapped by patchFields)
            cityCode: cityCode,
            cityName: cityLabel,
            state: state,
            // Redux fields (direct mapping)
            [DealFields.accounts__business_city_label]: cityLabel,
            [DealFields.accounts__business_stateprovince]: state
          }

          // Only include countryCode if we're selecting a city (not clearing)
          if (!isClearing && countryCode !== undefined) {
            result.countryCode = countryCode
            result[DealFields.accounts__business_country_code] = countryCode
          }

          return result
        }
      }

    case FieldTypeEnum.COUNTRY:
      return {
        ...baseConfig,
        contextKey: `cell-${record.id}-${columnKey}`,
        // Multi-field PATCH: Send country code and clear city
        patchFields: [
          { sourceKey: 'countryCode', targetField: 'business_country_code' },
          { sourceKey: 'cityCode', targetField: 'business_city_code' },
          { sourceKey: 'state', targetField: 'business_stateprovince' }
        ],
        patchEndpoint: 'accounts',
        // Transform country value and clear city and state
        valueTransformer: (countryObject: any): CountryChangePatch => {
          // Extract country code from CountrySelect object: { label, value }
          // Handle clearing: when countryObject is empty string/null/undefined, set countryCode to null explicitly
          const isClearing =
            !countryObject || countryObject === '' || (typeof countryObject === 'object' && !countryObject.value)
          const countryCode = isClearing ? null : (countryObject?.value ?? null)
          const countryLabel = isClearing ? null : (countryObject?.label ?? null)

          return {
            // API fields (mapped by patchFields)
            // Explicitly set to null when clearing (not undefined) so it's included in JSON
            countryCode: countryCode,
            cityCode: null, // Clear city when country changes
            state: null, // Clear state when country changes
            // Redux fields (direct mapping) - include both code and label for state updates
            [DealFields.accounts__business_country_code]: countryCode,
            [DealFields.accounts__business_country_label]: countryLabel,
            [DealFields.accounts__business_city_label]: null,
            [DealFields.accounts__business_stateprovince]: null
          }
        }
      }

    case FieldTypeEnum.VALUATION:
      const valuationConfig: any = {
        ...baseConfig,
        currencySymbol: getCurrencySymbol(),
        preMoneyValue: record[DealFields.rounds__pre_money_valuation] || null,
        postMoneyValue: record[DealFields.rounds__post_money_valuation] || null,
        min: 0,
        step: 0.01,
        showValuationType: true,
        // Multi-field PATCH: Send both pre and post money valuations
        patchFields: [
          { sourceKey: 'preMoneyValue', targetField: 'pre_money_valuation' },
          { sourceKey: 'postMoneyValue', targetField: 'post_money_valuation' }
        ],
        patchEndpoint: 'rounds',
        // Transform valuation object for API + Redux
        valueTransformer: (value: any, currentRecord: any) => {
          // value is the valuation object with preMoneyValue and postMoneyValue
          return {
            // API fields (mapped by patchFields)
            preMoneyValue: value?.preMoneyValue || null,
            postMoneyValue: value?.postMoneyValue || null,
            // Redux fields (direct mapping)
            [DealFields.rounds__pre_money_valuation]: value?.preMoneyValue || null,
            [DealFields.rounds__post_money_valuation]: value?.postMoneyValue || null
          }
        }
      }
      // Set patchId to roundId for Round fields
      if (roundId) {
        valuationConfig.patchId = roundId
      }
      return valuationConfig

    case FieldTypeEnum.URL:
      return {
        ...baseConfig,
        placeholder: 'Enter website URL...'
      }

    case 'textarea':
      return {
        ...baseConfig,
        placeholder: 'Enter description...',
        rows: 3
      }

    default:
      return baseConfig
  }
}

/**
 * Maps validation errors from DealsTable format to InputManager format
 */
export function mapValidationErrors(cellEditErrors: Record<string, string>, recordId: string): Record<string, string> {
  const validationErrors: Record<string, string> = {}

  Object.keys(cellEditErrors).forEach((errorKey) => {
    if (errorKey.startsWith(`${recordId}_`)) {
      const fieldKey = errorKey.replace(`${recordId}_`, '')
      validationErrors[fieldKey] = cellEditErrors[errorKey]
    }
  })

  return validationErrors
}

/**
 * Handles auto-population logic for related fields
 */
export function handleAutoPopulation(
  fieldKey: string,
  newValue: any,
  record: any,
  updateField: (key: string, value: any) => void,
  helpers: any
): void {
  // City selection auto-populates country and state
  if (fieldKey === 'accounts__business_city_label' && newValue) {
    if (newValue.countryCode) {
      updateField('accounts__business_country_label', newValue.countryCode)
    }
    const state = newValue.data?.state ?? newValue.state ?? null
    if (state !== undefined && state !== null) {
      updateField('accounts__business_stateprovince', state)
    }
  }
}
